import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Button,
  Link,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Collapse
} from '@mui/material';
import { ExpandMore, Menu as MenuIcon, Facebook, Instagram, LinkedIn, YouTube, Close, ExpandLess } from '@mui/icons-material';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { havenColors } from '../../theme/theme';
import logo from '../../assets/images/HAVEN-logo.png';
import havenProLogo from '../../assets/images/HavenRGB-PRO w_grad horz.svg';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [solutionsMenu, setSolutionsMenu] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);

  const handleSolutionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSolutionsMenu(event.currentTarget);
  };

  const handleSolutionsClose = () => {
    setSolutionsMenu(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileSolutionsOpen(false);
  };

  const handleMobileSolutionsToggle = () => {
    setMobileSolutionsOpen(!mobileSolutionsOpen);
  };

  const solutionPages = [
    { title: 'Demand Control Ventilation', path: '/residential-demand-control-ventilation' },
          { title: 'Active Air Management', path: '/haven-products' },
    { title: 'Radon Mitigation', path: '/smart-home-radon-mitigation' }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
                  sx={{ 
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderBottom: `1px solid #E5E5E5`
          }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Link
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: '#9BB3BF'
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="HAVEN"
              sx={{
                height: 55
              }}
            />
          </Link>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
            <Button
              onClick={handleSolutionsClick}
              endIcon={<ExpandMore />}
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Solutions
            </Button>
            <Menu
              anchorEl={solutionsMenu}
              open={Boolean(solutionsMenu)}
              onClose={handleSolutionsClose}
              sx={{ mt: 1 }}
            >
              {solutionPages.map((page) => (
                <MenuItem
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleSolutionsClose}
                  sx={{
                    color: '#9BB3BF',
                    '&:hover': {
                      backgroundColor: `${havenColors.primaryTeal}10`
                    }
                  }}
                >
                  {page.title}
                </MenuItem>
              ))}
            </Menu>
            
            <Button 
              component={RouterLink} 
              to="/homeowner-store"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: location.pathname === '/homeowner-store' ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Store
            </Button>
            
            <Button 
              component={RouterLink} 
              to="/resources"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: location.pathname === '/resources' ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              Resources
            </Button>
            
            <Button 
              component={RouterLink} 
              to="/about"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '1rem',
                color: '#9BB3BF',
                fontFamily: 'Rubik, sans-serif',
                backgroundColor: location.pathname === '/about' ? `${havenColors.primaryTeal}10` : 'transparent',
                '&:hover': { backgroundColor: `${havenColors.primaryTeal}10` }
              }}
            >
              About
            </Button>
            

            
            <Link
              href="https://pro.haveniaq.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                backgroundColor: 'rgba(155, 179, 191, 0.1)',
                border: '1px solid rgba(155, 179, 191, 0.3)',
                borderRadius: '8px',
                padding: '8px 12px',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  backgroundColor: 'rgba(155, 179, 191, 0.2)',
                  borderColor: 'rgba(155, 179, 191, 0.5)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(155, 179, 191, 0.2)'
                }
              }}
            >
              <Box
                component="img"
                src={havenProLogo}
                alt="HAVEN Pro"
                sx={{
                  height: 32,
                  width: 'auto',
                  mr: 1
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  color: '#9BB3BF',
                  fontFamily: 'Rubik, sans-serif',
                  letterSpacing: '0.5px'
                }}
              >
                PRO LOGIN
              </Typography>
            </Link>
          </Box>

          <IconButton
            onClick={handleMobileMenuToggle}
            sx={{ display: { xs: 'block', md: 'none' }, color: '#9BB3BF' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: { width: '280px' }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="HAVEN"
              sx={{ height: 40 }}
            />
            <IconButton onClick={handleMobileMenuClose}>
              <Close />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <List>
            {/* Solutions Menu */}
            <ListItemButton onClick={handleMobileSolutionsToggle}>
              <ListItemText primary="Solutions" />
              {mobileSolutionsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={mobileSolutionsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {solutionPages.map((page) => (
                  <ListItemButton 
                    key={page.path}
                    component={RouterLink} 
                    to={page.path}
                    onClick={handleMobileMenuClose}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText primary={page.title} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            
            {/* Store */}
            <ListItemButton 
              component={RouterLink} 
              to="/homeowner-store"
              onClick={handleMobileMenuClose}
            >
              <ListItemText primary="Store" />
            </ListItemButton>
            
            {/* Resources */}
            <ListItemButton 
              component={RouterLink} 
              to="/resources"
              onClick={handleMobileMenuClose}
            >
              <ListItemText primary="Resources" />
            </ListItemButton>
            
            {/* About */}
            <ListItemButton 
              component={RouterLink} 
              to="/about"
              onClick={handleMobileMenuClose}
            >
              <ListItemText primary="About" />
            </ListItemButton>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Pro Portal */}
            <ListItemButton 
              component="a"
              href="https://pro.haveniaq.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleMobileMenuClose}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box
                  component="img"
                  src={havenProLogo}
                  alt="HAVEN Pro"
                  sx={{ height: 24, mr: 1 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  PRO LOGIN
                </Typography>
              </Box>
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 6,
          backgroundColor: havenColors.darkGray,
          color: 'white',
          mt: 'auto'
        }}
      >
        <Container maxWidth="lg">
          {/* Footer Content */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, mb: 4 }}>
            {/* Logo Section */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="HAVEN"
                  sx={{
                    height: 48,
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </Box>
            </Box>
            
            {/* Homeowners Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Homeowners</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link href="/homeowner-start" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  HAVEN for Homeowners
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link href="/resources" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Support for Homeowners
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Phone: 1-888-96-HAVEN
              </Typography>
              {/* Social Media Icons for Homeowners */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton 
                  component="a"
                  href="https://www.facebook.com/haven.tzoa"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Facebook sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.instagram.com/haven_tzoa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Instagram sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.linkedin.com/company/3521229"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <LinkedIn sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Professionals Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Professionals</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link href="/become-a-pro" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  HAVEN for HVAC Pros
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  href="https://haveniaq.com/haven-partners/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                HAVEN Partners
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  href="https://haveniaq.com/contact/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                Support for Pros
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  href="https://support.pro.haveniaq.com/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                Pro Help Center
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Phone: 1-833-4-IAQ-PRO
              </Typography>
              {/* Social Media Icons for Professionals */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <IconButton 
                  component="a"
                  href="https://www.facebook.com/HAVENProIAQ/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Facebook sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.instagram.com/haven_pro/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <Instagram sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.linkedin.com/company/haveniaq/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <LinkedIn sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton 
                  component="a"
                  href="https://www.youtube.com/channel/UCOuahYaxJHMoDZphUiSnmuQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    backgroundColor: havenColors.primaryTeal,
                    color: 'white',
                    '&:hover': { backgroundColor: havenColors.primaryTeal, opacity: 0.8 }
                  }}>
                  <YouTube sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Company Column */}
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Company</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link href="/about" sx={{ color: 'inherit', textDecoration: 'none' }}>
                  Our Team
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Link 
                  href="https://ca.indeed.com/cmp/Tzoa?from=univSerpAboutLink&attributionid=univSerpCompanyLogo" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                Careers
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                HAVEN HQ
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                555 W Hastings St<br />
                Suite #1200<br />
                Vancouver, BC V6B 4N6<br />
                Canada
              </Typography>
            </Box>
          </Box>

          {/* Copyright */}
          <Box sx={{ 
            textAlign: 'center', 
            pt: 3, 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            opacity: 0.8 
          }}>
            <Typography variant="body2">
              Copyright © 2025 HAVEN IAQ
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout; 